import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { getAuctionUser } from '../redux/selectors'
import { getAuctionUserApi, getAuctionByCode } from '../api'
import { showLoading } from 'services/util'

import css from './sass/mypages.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import AuctionEndedCard from '../auctions/AuctionEndedCard'
import MyItemRow from './MyItemRow'
import MyItemCard from './MyItemCard'
import MyFixedItemCard from './MyFixedItemCard'
import { calculateUserBalance, userHasCharge } from '../util/endedAuctionUtils'
import MyFixedItemRow from './MyFixedItemRow'
import MyDonationRow from './MyDonationRow'
import AuctionEndedCardPaid from '../auctions/AuctionEndedCardPaid'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import MyUpcomingItemCard from './MyUpcomingItemCard'

const sectionVisibilityStorageKey = 'myItemsSectionVisibility'
const defaultSectionVisibility = {
  upcoming: true,
  live: true,
  past: true,
}
const getSectionContentId = (sectionKey) => `myitems-section-${sectionKey}`

export default function MyItems() {
  const [pastAuctionList, setPastAuctionList] = useState([])
  const [stillOweAuctionList, setStillOweAuctionList] = useState([])
  const [allPaidAuctionList, setAllPaidAuctionList] = useState([])
  const [currentAuctionList, setCurrentAuctionList] = useState([])
  const [currentFavItems, setCurrentFavItems] = useState([])

  const [upcomingDisplay, setUpcomingDisplay] = useState([])
  const [userDisplay, setUserDisplay] = useState([])

  const [noItems, setNoItems] = useState(true)
  const [sectionVisibility, setSectionVisibility] = useState(() => {
    if (typeof window === 'undefined') return defaultSectionVisibility

    try {
      const storedValue = window.sessionStorage.getItem(sectionVisibilityStorageKey)

      if (!storedValue) return defaultSectionVisibility

      return {
        ...defaultSectionVisibility,
        ...JSON.parse(storedValue),
      }
    } catch {
      return defaultSectionVisibility
    }
  })
  
  const auctionUser = useSelector(getAuctionUser)
  const currentUserId = auctionUser?.UserId

  const toggleSectionVisibility = (sectionKey) => {
    setSectionVisibility((currentVisibility) => ({
      ...currentVisibility,
      [sectionKey]: !currentVisibility[sectionKey],
    }))
  }

  const renderSectionToggle = (sectionKey) => {
    const isVisible = sectionVisibility[sectionKey]

    return (
      <button
        type='button'
        className={`${themeCss['button']} ${isVisible ? themeCss['StrokeBlue'] : themeCss['FilledBlue']} ${css['myitems-toggle-button']}`}
        aria-expanded={isVisible}
        aria-controls={getSectionContentId(sectionKey)}
        onClick={() => toggleSectionVisibility(sectionKey)}
      >
        {isVisible ? 'Hide' : 'Show'}
      </button>
    )
  }

  const buildAuctionDisplayData = (registration) => {
    return {
      ...registration.Auction,
      Donations: registration.Donations,
      DonationTotal: registration.DonationTotal,
      DonationPayment: registration.DonationPayment,
      Favorites: registration.Favorites,
    }
  }

  useEffect(() => {
    async function refreshAuctionUser() {
      showLoading(true)
      // updates auctionUser in redux/localStorage
      await getAuctionUserApi()
      showLoading(false)
    }

    refreshAuctionUser()
  }, [])

  useEffect(() => {
    const favIds = []
    
    auctionUser.MyAuctions.forEach((reg) => {
      if (reg.Favorites?.length > 0) {
        favIds.push(...reg.Favorites)
      }
      })

    setCurrentFavItems(favIds)

  }, [auctionUser])

  useEffect(() => {
    const doAuctions = async () => {
        if (!auctionUser.MyAuctions)
        return

      const now = new Date()

      const liveAuctions = auctionUser.MyAuctions.map(buildAuctionDisplayData)
                                                    .filter(auction => auction.StartTime < now.toISOString() && auction.EndTime > now.toISOString())

      for (const auction of liveAuctions) {
        // get full auction details for each auction, so we can get all Bids and all Items (myAuctions currently only shows items the user is winning)
        const currentAuctionDetails = await getAuctionByCode(auction.AuctionCode)
        auction.Bids = currentAuctionDetails.Bids
        auction.AllItems = currentAuctionDetails.Items
        auction.FixedItems = currentAuctionDetails.FixedItems
      }

      setCurrentAuctionList(liveAuctions)

      const userAuctions = auctionUser.MyAuctions.map(buildAuctionDisplayData)
                                                     .sort((a, b) => (a.StartTime < b.StartTime) ? -1 : (a.StartTime > b.StartTime) ? 1 : 0)
                                                     .map(auction => { return {...auction, 'displayData': false}})

      const upcomingAuctions = userAuctions.filter((auction) => {
        return auction.StartTime > now.toISOString()
      })

      for (const auction of upcomingAuctions) {
        // get full auction details for each auction, so we can get all Items
        // filters out only favorites for all items
        const currentAuctionDetails = await getAuctionByCode(auction.AuctionCode)
        auction.Items = currentAuctionDetails.Items.filter((item) => auction.Favorites?.includes(item.ItemId))
        auction.FixedItems = currentAuctionDetails.FixedItems.filter((item) => auction.Favorites?.includes(item.FixedItemId))
      }

      setUpcomingDisplay(upcomingAuctions)

      const pastAuctions = userAuctions.filter((auction) => {
        return auction.EndTime < now.toISOString()
      })

      setPastAuctionList(pastAuctions)
    }

    doAuctions()

  }, [auctionUser.MyAuctions])

  useEffect(() => {
    if (currentAuctionList.length > 0) {

      // TO DO clean up for efficiency and display efficiency

      let myCurrentDisplay = []

      currentAuctionList.forEach(auction => {
        // Loop through each item in the auction's AllItems
        
        let auctionItems = { 
          currentWinning: [],
          currentOutbid: [],
          currentFav: [],
          fixedItems: []
        }

        auction.AllItems.forEach(item => {
          // Add the "currentlyWinning" property
          if (item.CurrentHighBidder === auctionUser.UserId) {
            item.currentlyWinning = true // User has the highest bid
            auctionItems.currentWinning.push(item)
          } 
          // if they are not currently winning the item, did they bid on it before?
          else if (item.BidCount > 0 && item.CurrentHighBidder !== auctionUser.UserId && auction.Bids.some(bid => bid.ItemId === item.ItemId && bid.UserId === auctionUser.UserId)) {
            item.currentlyWinning = false // User has bid, but not the highest bidder
            auctionItems.currentOutbid.push(item)
          }
          //if they have not bid on it, but marks it as a favorite
          // Add the "isFav" property
          else if (currentFavItems.includes(item.ItemId)) {
            // item.isFav = true // Item is a favorite
            auctionItems.currentFav.push(item)
          }
        })

        auction.FixedItems.forEach(item => {
          // TO DO - would need to sort here if we want to move the favorited fixed items at the front or end of the display
          auctionItems.fixedItems.push(item)
        })

        //TO DO: clean up userItems - don't think we need this anymore
        const myItems = [...auctionItems.currentOutbid, ...auctionItems.currentWinning, ...auctionItems.currentFav]
        myCurrentDisplay.push({auction: auction, userItems: auctionItems, myItems: myItems, fixedItems: auctionItems.fixedItems})
      })
      setUserDisplay(myCurrentDisplay)

    }
  }, [currentAuctionList, auctionUser, currentFavItems])

  useEffect(() => {
    
    if (pastAuctionList) {
      
      let allPaid = []
      let stillOwe = []
      
      for (let i = 0; i < pastAuctionList.length; i++) {
        let auction = pastAuctionList[i]
        const outstandingBalance = calculateUserBalance(auctionUser, auction)

        if (outstandingBalance?.total > 0) {
          auction.outstandingBalance = outstandingBalance
          stillOwe.push(auction)
        } else {
          allPaid.push(auction)
        }
      }

      setStillOweAuctionList(stillOwe)
      setAllPaidAuctionList(allPaid)
    }
  }, [pastAuctionList, auctionUser])

  useEffect(() => {

    if (stillOweAuctionList.length === 0 && allPaidAuctionList.length === 0) {
      setNoItems(true)
    } else {
      setNoItems(false)
    }

  }, [stillOweAuctionList, allPaidAuctionList])

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.sessionStorage.setItem(sectionVisibilityStorageKey, JSON.stringify(sectionVisibility))
  }, [sectionVisibility])

  return (
    <div>
      <div className={`${css['mypages-header']} ${css['myitems-section-header']}`}>
        <h2 className={css['mypages-headings']}>Upcoming Auctions</h2>
        {renderSectionToggle('upcoming')}
      </div>
      <div
        id={getSectionContentId('upcoming')}
        aria-hidden={!sectionVisibility.upcoming}
        className={`${css['myitems-section-content']} ${!sectionVisibility.upcoming ? css['myitems-section-content-hidden'] : ''}`}
      >
        <div className={css['myitems-section-content-inner']}>
          <div className={themeCss['blue-background']}>
            {upcomingDisplay.length > 0 && upcomingDisplay.map((auction) => {
              return (
                <div key={auction.AuctionCode} className={css['myitems-sections']}>
                  <h5>
                    <Link className={`${themeCss['bold-nodecoration']} ${themeCss['auction-title-link']}`} to={{
                        pathname: '/auctionhome',
                        search: `?auctionId=${auction.AuctionCode}`,
                        auction
                      }}> {auction.Title} </Link>
                    </h5>

                  <div className={css['myitems-row-cards']}>
                    {auction.Items.length > 0 && auction.Items.map((item) => {
                      return( <MyUpcomingItemCard key={item.ItemCode} item={item} auction={auction} 
                        currentUserId={currentUserId} />)
                    })}
                    {auction.FixedItems.length > 0 && auction.FixedItems.map((item) => {
                      return( <MyUpcomingItemCard key={item.ItemCode} item={item} auction={auction} 
                        currentFavItems={currentFavItems} currentUserId={currentUserId} />)
                    })}
                    
                    { (auction.Items.length === 0 && auction.FixedItems.length === 0)
                      && <p className={css['myitems-emptyrow']}>Add some items to your favorites to track them here.</p>
                    }
                    
                  </div>
                </div>
              )})}
            </div>
        </div>
      </div>

      <div className={`${css['mypages-header']} ${css['myitems-section-header']}`}>
        <h2 className={css['mypages-headings']}>Live Auction Items</h2>
        {renderSectionToggle('live')}
      </div>
      <div
        id={getSectionContentId('live')}
        aria-hidden={!sectionVisibility.live}
        className={`${css['myitems-section-content']} ${!sectionVisibility.live ? css['myitems-section-content-hidden'] : ''}`}
      >
        <div className={css['myitems-section-content-inner']}>
          <div className={themeCss['blue-background']}>
            {userDisplay.length > 0 && userDisplay.map((auction) => {
              return (
                <div key={auction.auction.AuctionCode} className={css['myitems-sections']}>
                  <h5>
                    <Link className={`${themeCss['bold-nodecoration']} ${themeCss['auction-title-link']}`} to={{
                        pathname: '/auctionhome',
                        search: `?auctionId=${auction.auction.AuctionCode}`,
                        auction
                      }}> {auction.auction.Title} </Link>
                    </h5>

                  <div className={css['myitems-row-cards']}>
                    {auction.myItems.length > 0 && auction.myItems.map((item) => {
                      return( <MyItemCard key={item.ItemCode} item={item} auction={auction.auction} 
                        currentUserId={currentUserId} />)
                    })}
                    {auction.fixedItems.length > 0 && auction.fixedItems.map((item) => {
                      return( <MyFixedItemCard key={item.ItemCode} item={item} auction={auction.auction} 
                        currentFavItems={currentFavItems} currentUserId={currentUserId} />)
                    })}
                    
                    { (auction.myItems.length === 0 && auction.fixedItems.length === 0)
                      && <p className={css['myitems-emptyrow']}>Add some items to your favorites to track them here.</p>
                    }
                    
                    
                  </div>
                </div>
              )})}
            </div>
        </div>
      </div>
      
      <div className={`${css['mypages-header']} ${css['myitems-section-header']}`}>
        <div className={css['myitems-section-heading-copy']}>
          <h2 className={css['mypages-headings']}>My Past Items</h2>
          {/* {!noItems && <p>Below are the items you've won or donations made, organized by auction.</p>} */}
        </div>
        {renderSectionToggle('past')}
      </div>

      <div
        id={getSectionContentId('past')}
        aria-hidden={!sectionVisibility.past}
        className={`${css['myitems-section-content']} ${!sectionVisibility.past ? css['myitems-section-content-hidden'] : ''}`}
      >
        <div className={css['myitems-section-content-inner']}>
          {!noItems && <p className={css['mypages-header']}> Below are the items you've won or donations made, organized by auction.</p>}
          <div className={themeCss['blue-background']}>
            {stillOweAuctionList?.length > 0 && stillOweAuctionList.map((auction) => {
              return (
                <div key={auction.AuctionCode} className={css['mypages-card']}>
                  <h3 className={css['mypages-headings']}>
                    <Link className={`${themeCss['bold-nodecoration']} ${themeCss['auction-title-link']}`} to={{
                        pathname: '/auctionhome',
                        search: `?auctionId=${auction.AuctionCode}`,
                        auction
                      }}> {auction.Title}</Link>
                    </h3>
                  <AuctionEndedCard auction={auction} auctionUser={auctionUser} outstandingBalance={auction.outstandingBalance} />
                  {auction?.Items?.length > 0 && auction?.Items?.map(item => <MyItemRow key={item.ItemId || item.ItemCode} item={item} /> )}
                  {auction?.FixedItems?.length > 0 && auction?.FixedItems?.map(item => <MyFixedItemRow key={item.FixedItemId || item.ItemCode} item={item} currentUserId={currentUserId} /> )}
                  {auction?.DonationTotal > 0 && <MyDonationRow auction={auction} /> }
                </div>
              )})}
              {allPaidAuctionList?.length > 0 && allPaidAuctionList.map((auction) => {

              // if user didn't have items or donate, show nothing
              if (!userHasCharge(auctionUser, auction)) return null

              return (
                <div key={auction.AuctionCode}>
                  <div className={css['mypages-card']}>
                    <h3 className={css['mypages-headings']}>
                      <Link className={`${themeCss['bold-nodecoration']} ${themeCss['auction-title-link']}`} to={{
                          pathname: '/auctionhome',
                          search: `?auctionId=${auction.AuctionCode}`,
                          auction
                        }}> {auction.Title}</Link>
                      </h3>
                    <AuctionEndedCardPaid auction={auction} auctionUser={auctionUser}/>
                    {auction?.Items?.length > 0 && auction?.Items?.map(item => <MyItemRow key={item.ItemId || item.ItemCode} item={item} /> )}
                    {auction?.FixedItems?.length > 0 && auction?.FixedItems?.map(item => <MyFixedItemRow key={item.FixedItemId || item.ItemCode} item={item} currentUserId={currentUserId} /> )}
                    {auction?.DonationTotal > 0 && <MyDonationRow auction={auction} /> }
                  </div>
                </div>
              )})}
              {noItems && <div>
                  <div className={css['mypages-card']}>
                    <h3 className={css['mypages-headings']}>You have won no items yet.</h3>
                  </div>
                </div>}
              </div>
        </div>
      </div>
    </div>
  )
}
